import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Lenis from "lenis";
import { ArrowLeft, ArrowUpRight, Droplet, Layers, ShieldAlert, Activity, Sprout, Crown, LineChart, ClipboardCheck, FlaskConical, ScrollText } from "lucide-react";
import FloatingBookButton from "../components/FloatingBookButton";

const SCAN_URL = "https://scalp.locticiancarlocs.co.za";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/fdlrkebv_WhatsApp%20Image%202025-11-21%20at%2012.44.06_ff66975e.jpg";

const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/cd3ppkeh_Screenshot_20260318_170347_Instagram.jpg",
  why: "https://customer-assets.emergentagent.com/job_loctician-hub/artifacts/50z5czmb_Screenshot_20260318_170331_Instagram.jpg",
};

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}>
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}>
    {children}
  </motion.div>
);

const ScanButton = ({ children, primary = true, testid }) => (
  <a href={SCAN_URL} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-xs font-semibold uppercase tracking-widest transition-all duration-300 hover:-translate-y-1
      ${primary ? 'bg-[#D4AF37] text-[#050505] hover:bg-[#e8c65a]' : 'border border-white/20 text-white hover:border-[#D4AF37] hover:text-[#D4AF37]'}`}>
    {children}
    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
  </a>
);

const ScalpAnalyzerPage = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    document.title = "Scalp Analyzer | Precision Loc & Scalp Diagnostics — Loctician Carlocs";
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    function raf(t) { lenis.raf(t); requestAnimationFrame(raf); }
    const id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); document.title = "Loctician Carlocs | Luxury Loc Care in South Africa"; };
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  const categories = [
    { icon: Droplet, name: "Hydration", desc: "Moisture balance across your scalp and locs." },
    { icon: Layers, name: "Buildup", desc: "Product and residue accumulation within the locs." },
    { icon: ShieldAlert, name: "Irritation", desc: "Signs of tension, dryness or inflammation." },
    { icon: Activity, name: "Density", desc: "Fullness and distribution across the scalp." },
    { icon: Sprout, name: "Loc Stage", desc: "Where your locs sit in their maturity journey." },
  ];

  const reportItems = [
    { icon: ClipboardCheck, title: "Category Ratings", desc: "Clear ratings across hydration, buildup, irritation, density and loc stage." },
    { icon: FlaskConical, title: "Product Recommendations", desc: "Targeted product guidance matched to your scalp's real condition." },
    { icon: ScrollText, title: "Aftercare Tips", desc: "Practical loc-care routines grounded in professional loctician expertise." },
  ];

  const steps = [
    { num: "01", title: "Capture", desc: "Take guided photos of your scalp and locs following simple on-screen prompts." },
    { num: "02", title: "Questionnaire", desc: "Answer a short set of questions about your loc journey and routine." },
    { num: "03", title: "Your Report", desc: "Receive a detailed report with category ratings, recommendations and aftercare." },
  ];

  return (
    <div className="bg-[#050505] text-white overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Loctician Carlocs - Johannesburg loctician logo" className="h-11 w-auto" />
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors text-xs uppercase tracking-widest">
              <ArrowLeft className="w-4 h-4" /> Home
            </Link>
            <a href={SCAN_URL} target="_blank" rel="noopener noreferrer" data-testid="nav-scan-btn"
              className="bg-[#D4AF37] text-[#050505] px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#e8c65a] transition-colors">
              Start Your Scan
            </a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y: heroImgY }} className="absolute inset-0 -z-10">
          <img src={IMG.hero} alt="Johannesburg locs scalp analysis" className="w-full h-[120%] object-cover object-center opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/75 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="max-w-7xl mx-auto px-6 pt-32 pb-20 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="text-[#D4AF37] text-xs uppercase tracking-[0.4em] mb-8">
            Carlocs Scalp Analyzer
          </motion.p>

          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight mb-8">
            <MaskLine delay={0.3}>Discover Your</MaskLine>
            <MaskLine delay={0.45}><span className="italic text-[#D4AF37]">Scalp Health</span></MaskLine>
            <MaskLine delay={0.6}>Precision Analysis Built for Locs</MaskLine>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="text-white/60 text-base md:text-lg font-light max-w-xl mb-8 leading-relaxed">
            Advanced photo-based scalp analysis, made exclusively for people with locs. Paired with a short questionnaire, it delivers a detailed report — grounded in real loctician expertise.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <ScanButton testid="hero-scan-btn">Start Your Scan</ScanButton>
            <span className="text-white/50 text-sm">Scan fee: <span className="text-[#D4AF37] font-medium">R79</span></span>
          </motion.div>
        </motion.div>
      </section>

      {/* WHAT YOU GET — REPORT */}
      <section className="py-24 md:py-32 px-6 bg-[#f4f1ea] text-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-[#9a7d1f] text-xs uppercase tracking-[0.3em] mb-4">Your Detailed Report</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
                Real diagnostic insight, <span className="italic">not guesswork</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {reportItems.map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-black/5 hover:shadow-xl transition-shadow duration-500">
                <span className="w-12 h-12 rounded-full bg-[#0a0a0a] flex items-center justify-center mb-6">
                  <item.icon className="w-5 h-5 text-[#D4AF37]" />
                </span>
                <h3 className="font-serif text-xl mb-3">{item.title}</h3>
                <p className="text-black/50 text-sm font-light leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORY RATINGS */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">Category Ratings</p>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight">
                Five Dimensions of <span className="italic text-[#D4AF37]">Loc Health</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {categories.map((c, i) => (
              <motion.div key={c.name} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="group rounded-2xl border border-white/10 p-7 hover:border-[#D4AF37]/50 hover:-translate-y-1 transition-all duration-500">
                <c.icon className="w-8 h-8 text-[#D4AF37] mb-5" />
                <h3 className="font-serif text-lg mb-2">{c.name}</h3>
                <p className="text-white/45 text-sm font-light leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CROWN SCORE + PROGRESS */}
      <section className="py-24 md:py-32 px-6 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div>
              <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-4">The Crown Score</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-6 leading-tight">
                One number that captures your <span className="italic text-[#D4AF37]">loc journey</span>
              </h2>
              <p className="text-white/55 font-light text-lg leading-relaxed mb-8">
                Every scan generates your Crown Score — a single, easy-to-read measure of your overall scalp and loc health. Rescan over time and watch your score evolve as your routine improves.
              </p>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <Crown className="w-6 h-6 text-[#D4AF37]" />
                  <span className="text-white/70 text-sm">Crown Score</span>
                </div>
                <div className="flex items-center gap-3">
                  <LineChart className="w-6 h-6 text-[#D4AF37]" />
                  <span className="text-white/70 text-sm">Progress Tracking</span>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#171310] to-[#0a0a0a] p-12 text-center">
              <Crown className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
              <p className="text-white/40 text-xs uppercase tracking-[0.3em] mb-4">Your Crown Score</p>
              <p className="font-serif text-7xl md:text-8xl text-[#D4AF37] leading-none mb-4">88</p>
              <p className="text-white/50 text-sm">Tracked scan after scan, month after month.</p>
              <div className="mt-8 h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div className="h-full bg-[#D4AF37]" initial={{ width: 0 }} whileInView={{ width: "88%" }}
                  viewport={{ once: true }} transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-16 text-center">
              How It <span className="italic text-[#D4AF37]">Works</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <motion.div key={step.num} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="group rounded-2xl border border-white/10 p-8 hover:border-[#D4AF37]/50 transition-colors duration-500">
                <span className="font-serif text-5xl text-white/15 group-hover:text-[#D4AF37]/50 transition-colors duration-500">{step.num}</span>
                <h3 className="font-serif text-xl mt-4 mb-2">{step.title}</h3>
                <p className="text-white/50 text-sm font-light">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section className="relative py-28 md:py-40 px-6 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMG.why} alt="Johannesburg loctician expertise" className="w-full h-full object-cover object-center opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/75 to-[#050505]" />
        </div>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] mb-6">Why This Matters</p>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-8 leading-[1.05]">
              Backed by 12+ years of<br /><span className="italic text-[#D4AF37]">hands-on loctician expertise</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-white/60 text-lg font-light max-w-2xl mx-auto leading-relaxed">
              This isn&apos;t a generic scalp app. Every rating, recommendation and aftercare tip is shaped by real diagnostic experience gained over more than a decade of professional loc care. You get the kind of precise, loc-specific insight that only comes from an expert who has cared for thousands of crowns.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-40 px-6 bg-[#f4f1ea] text-[#0a0a0a]">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-8">
              Ready to understand your crown?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-black/60 text-lg md:text-xl font-light max-w-2xl mx-auto mb-6">
              Get your detailed scalp and loc report with your personal Crown Score.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-serif text-2xl md:text-3xl text-[#9a7d1f] mb-10">Scan fee: R79</p>
          </Reveal>
          <Reveal delay={0.2}>
            <a href={SCAN_URL} target="_blank" rel="noopener noreferrer" data-testid="final-scan-btn"
              className="group inline-flex items-center justify-center gap-2 bg-[#0a0a0a] text-white px-10 py-5 rounded-full text-xs font-semibold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-[#050505] transition-all duration-300 hover:-translate-y-1">
              Start Your Scan
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5 bg-[#050505]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/"><img src={LOGO_URL} alt="Loctician Carlocs - Johannesburg loctician logo" className="h-10 w-auto" /></Link>
          <p className="text-white/30 text-sm">© {new Date().getFullYear()} Loctician Carlocs. All rights reserved.</p>
          <Link to="/" className="text-white/50 hover:text-[#D4AF37] transition-colors text-sm">Return to Main Site</Link>
        </div>
      </footer>

      <FloatingBookButton />
    </div>
  );
};

export default ScalpAnalyzerPage;
